import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useBootstrap } from '@/lib/useBootstrap';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Building2, Check, ChevronsUpDown, Shield } from 'lucide-react';

const ACTIVE_KEY = 'hj_active_workspace';

export function WorkspaceSwitcher({ collapsed = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(() => { try { return localStorage.getItem(ACTIVE_KEY) || ''; } catch { return ''; } });
  const [open, setOpen] = useState(false);
  const { data: boot } = useBootstrap();

  useEffect(() => {
    // Workspaces arrive in the shell payload (2026-06 perf pass) — this used to
    // be its own request on every navigation.
    const ws = boot?.workspaces;
    if (!ws) return;
    setData(ws);
    const ms = ws.memberships || [];
    if (!activeId && ms.length) {
      const internal = ms.find(m => m.type === 'internal') || ms[0];
      try { localStorage.setItem(ACTIVE_KEY, internal.workspace_id); } catch { /* no-op */ }
      setActiveId(internal.workspace_id);
    }
  }, [boot]); // eslint-disable-line

  if (!data) return null;
  const memberships = data.memberships || [];
  const isSA = data.is_superadmin;
  // Nothing to switch and not a superadmin → don't render chrome.
  if (memberships.length <= 1 && !isSA) return null;

  const active = memberships.find(m => m.workspace_id === activeId) || memberships[0];
  const switchTo = (m) => {
    try { localStorage.setItem(ACTIVE_KEY, m.workspace_id); } catch { /* no-op */ }
    setActiveId(m.workspace_id);
    setOpen(false);
    if (m.type === 'internal') { navigate('/app/dashboard'); }
    else { navigate('/app/platform/workspaces'); } // client ATS shell arrives in Phase 2
  };

  return (
    <div className={collapsed ? 'px-1 pb-2' : 'px-3 pb-3'} data-testid="workspace-switcher">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="workspace-switcher-trigger"
            className={`w-full flex items-center gap-2 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.05] transition-all ${collapsed ? 'justify-center p-2' : 'px-3 py-2'}`}>
            <div className="w-6 h-6 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
              <Building2 className="w-3.5 h-3.5 text-violet-400" />
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-white truncate leading-tight">{active?.name || 'Workspace'}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{isSA ? 'Superadmin' : (active?.role || '').replace(/_/g, ' ')}</p>
                </div>
                <ChevronsUpDown className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-64 p-1 bg-surface-1 border-white/[0.08]" data-testid="workspace-switcher-menu">
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500">Workspaces</div>
          {memberships.map(m => (
            <button key={m.workspace_id} onClick={() => switchTo(m)} data-testid={`workspace-option-${m.slug}`}
              className="w-full flex items-center gap-2 px-2 py-1 rounded-lg text-sm text-zinc-300 hover:bg-white/[0.05] hover:text-white transition-colors">
              <Building2 className="w-3.5 h-3.5 text-zinc-500" />
              <span className="flex-1 text-left truncate">{m.name}</span>
              {m.workspace_id === activeId && <Check className="w-3.5 h-3.5 text-violet-400" />}
            </button>
          ))}
          {isSA && (
            <>
              <div className="my-1 border-t border-white/[0.06]" />
              <button onClick={() => { setOpen(false); navigate('/app/platform/workspaces'); }} data-testid="workspace-switcher-platform"
                className="w-full flex items-center gap-2 px-2 py-1 rounded-lg text-sm text-violet-300 hover:bg-violet-500/10 transition-colors">
                <Shield className="w-3.5 h-3.5" /> Platform Console
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default WorkspaceSwitcher;
